import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'
import { initSmoothScroll, scrollToTop } from '../../lib/smooth-scroll'
import { BackToTop } from './BackToTop'
import { Footer } from './Footer'
import { Header } from './Header'
import { PreviewNotice } from './PreviewNotice'

/**
 * Restores scroll to the top on navigation — a client-side router keeps the
 * scroll offset otherwise, which lands the visitor mid-page on a fresh route.
 *
 * Jumps rather than smooth-scrolls under `prefers-reduced-motion`. A ROUTE
 * CHANGE is also the one case where smooth is wrong on its own terms: the new
 * page is unrelated to the old one, so animating between them just delays it.
 * It is kept smooth only because the distance is usually small.
 */
const useScrollToTop = (): void => {
  const { pathname } = useLocation()

  useEffect(() => {
    scrollToTop(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [pathname])
}

/** Wheel-only momentum scrolling. Every guard is in `lib/smooth-scroll.ts`. */
const useSmoothScroll = (): void => {
  useEffect(() => initSmoothScroll(), [])
}

export const RootLayout = () => {
  const { t } = useTranslation()
  useSmoothScroll()
  useScrollToTop()

  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="skip-link rounded-md bg-primary px-4 py-2 type-body font-semibold text-primary-foreground">
        {t('common.skipToContent')}
      </a>

      <Header />

      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        {/*
          ⚠️ MOUNTED ONCE, HERE, AND NOT IN SIX PAGES — THAT IS THE WHOLE POINT.
          `PreviewNotice` decides for itself whether the current path is one of
          the website's app-backed routes, so turning it off later is one edit in
          that file (empty its `NOTICE_ROUTES`) rather than eleven. It renders
          `null` on every other route, including `/` and `/blog`.

          It is INSIDE `main` and ABOVE `Outlet` on purpose: it is page content,
          not chrome, and it must sit above the page's own `<h1>`. It is outside
          the skip link's target only in the sense that `#main` lands on it
          first, which is correct — somebody skipping to the content should read
          the caveat before the content.

          Docs/34 § 1b has the three states and both exit procedures.
        */}
        <PreviewNotice />
        <Outlet />
      </main>

      <Footer />

      {/*
        ⚠️ IT HAS TO BE HERE, AND NOT INSIDE `main` OR ANY PAGE.

        `BackToTop` uses `mix-blend-mode: difference` to invert itself against
        whatever is behind it, and a blended element composites against the
        backdrop of its PARENT STACKING CONTEXT. The chain above this point —
        `html` → `body` → `#root` → this `div` — creates none, so its backdrop
        is the whole painted page. Move it inside anything with a `transform`,
        a `filter`, an `opacity` below 1, `will-change` or `isolation` and it
        silently blends against that ancestor's empty box instead. There is no
        error; it just stops inverting. The full reasoning is in that file.
      */}
      <BackToTop />
    </div>
  )
}
